import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import { SKETCH_REGISTRY } from '../data/sketches.config';
import { SPELL_REGISTRY } from '../data/spells.config';
import { BOSS_REWARDS } from '../data/boss-rewards.config';
import { DUNGEON_COMPLETION_REWARDS } from '../data/dungeons.config';
import { useLoadoutStore } from '../store/loadoutSlice';
import { useSpellCatalogStore } from '../store/spellCatalogueSlice';
import { useUnlockedDungeonsStore } from '../store/unlockedDungeonsSlice';
import { useCharacterProgressionStore } from '../store/characterProgressionSlice';
import { cinematicService } from './CinematicService';
import type { UnlockGrants } from '../types/reward.types';

/**
 * Single entry point for unlocking permanent game content. Every channel that
 * hands out new content routes through here so the "unlock in the owning store
 * + play the showcase" logic lives in one place: scripted grants (boss first
 * kill, dungeon completion), shop purchases, and trinket level-ups (Commode
 * handlers).
 *
 * Each primitive unlocks in its store and, by default, plays the unlock
 * showcase; pass `{ showcase: false }` for a silent unlock. `grantUnlocks`
 * bundles several at once; `onBossFirstKill` / `onDungeonCompleted` are the
 * gameplay-event entry points. Each primitive is idempotent (returns `false`
 * when the content was already owned — no duplicate, no showcase).
 */

interface UnlockOptions {
  showcase?: boolean;
}

export function unlockSpell(
  spellId: string,
  { showcase = true }: UnlockOptions = {},
): boolean {
  if (
    !SPELL_REGISTRY[spellId] ||
    !useSpellCatalogStore.getState().unlock(spellId)
  ) {
    return false;
  }
  if (showcase) {
    cinematicService.showcaseUnlock({
      kind: 'item',
      id: `grimoire-${spellId}`,
    });
  }
  return true;
}

export function unlockMajor(
  id: string,
  { showcase = true }: UnlockOptions = {},
): boolean {
  if (
    !TRINKET_MAJOR_REGISTRY[id] ||
    !useLoadoutStore.getState().unlockMajor(id)
  ) {
    return false;
  }
  if (showcase) {
    cinematicService.showcaseUnlock({ kind: 'major', id });
  }
  return true;
}

export function unlockMinor(
  id: string,
  { showcase = true }: UnlockOptions = {},
): boolean {
  if (
    !TRINKET_MINOR_REGISTRY[id] ||
    !useLoadoutStore.getState().unlockMinor(id)
  ) {
    return false;
  }
  if (showcase) {
    cinematicService.showcaseUnlock({ kind: 'minor', id });
  }
  return true;
}

export function unlockSketch(
  id: string,
  { showcase = true }: UnlockOptions = {},
): boolean {
  if (!SKETCH_REGISTRY[id] || !useLoadoutStore.getState().unlockSketch(id)) {
    return false;
  }
  if (showcase) {
    cinematicService.showcaseUnlock({ kind: 'sketch', id });
  }
  return true;
}

export function unlockDungeon(
  id: string,
  { showcase = false }: UnlockOptions = {},
): boolean {
  const unlocked = useUnlockedDungeonsStore.getState().unlockDungeon(id);
  // `showcase` is reserved for a future dungeon-unlock showcase: there is no
  // cinematic 'dungeon' kind yet, so dungeons are unlocked silently for now.
  void showcase;
  return unlocked;
}

/** Bundle of permanent unlocks granted together, each with its own showcase. */
export function grantUnlocks(grants: UnlockGrants): void {
  for (const spellId of grants.spells ?? []) {
    unlockSpell(spellId);
  }
  for (const id of grants.majors ?? []) {
    unlockMajor(id);
  }
  for (const id of grants.minors ?? []) {
    unlockMinor(id);
  }
  for (const id of grants.sketches ?? []) {
    unlockSketch(id);
  }
}

/**
 * Claims a boss/mini-boss first-kill reward (cf. `BOSS_REWARDS`). Called from
 * `MonsterEntity.death()` at the end of the death animation. Idempotent — the
 * reward is delivered only on the first kill, tracked by
 * `characterProgressionSlice.bossRewardsClaimed`. Returns whether this was the first kill.
 */
export function onBossFirstKill(bossId: string): boolean {
  const reward = BOSS_REWARDS[bossId];
  if (!reward) {
    return false;
  }
  const wasFirstKill = useCharacterProgressionStore
    .getState()
    .claimBossReward(bossId);
  if (wasFirstKill) {
    // The spell reward is NOT granted here: it drops as a physical grimoire on
    // the boss's death (100% first kill, 25% afterwards, no showcase — see
    // `MonsterEntity.death`). Only the non-spell rewards unlock directly here,
    // each with its showcase.
    grantUnlocks({
      majors: reward.grantsMajor ? [reward.grantsMajor] : undefined,
      minors: reward.grantsMinor ? [reward.grantsMinor] : undefined,
      sketches: reward.grantsSketch ? [reward.grantsSketch] : undefined,
    });
  }
  return wasFirstKill;
}

/**
 * Applies a dungeon's completion reward the first time it is cleared: unlocks
 * the next dungeons, hands out the guaranteed grimoire items, and grants the
 * scripted trinket/sketch unlocks. No-op if already completed or if no reward
 * is defined for the dungeon.
 */
export function onDungeonCompleted(dungeonId: string): void {
  if (!useUnlockedDungeonsStore.getState().markCompleted(dungeonId)) {
    return;
  }
  const reward = DUNGEON_COMPLETION_REWARDS[dungeonId];
  if (!reward) {
    return;
  }
  for (const id of reward.unlocks) {
    unlockDungeon(id);
  }
  // Dungeon-completion spells unlock DIRECTLY (no grimoire item), each with its
  // unlock showcase — unlike boss first-kills, which drop a grimoire on the
  // ground (cf. `MonsterEntity.death`).
  for (const spellId of reward.grantSpells ?? []) {
    unlockSpell(spellId);
  }
  grantUnlocks({
    majors: reward.grantMajors,
    minors: reward.grantMinors,
    sketches: reward.grantSketches,
  });
}
